import React, { useEffect, useState, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { checkFTUERequired } from '../../services/userService';
import LoadingIndicator from '../loading-indicator/LoadingIndicator';
import { useMyJoinRequest } from '../join-requests/useMyJoinRequest';
import { POLICY_PAGE_PATH_LIST } from '../policy/policyPaths';
import { useTranslation } from 'react-i18next';

interface FTUEGuardProps {
  children: React.ReactNode;
}

const SKIP_PATHS = ['/getting-started', '/login', '/signup', '/pricing', '/forgot-password', '/reset-password', '/sso', '/session-expired', '/device-setup', '/marketplace-signup', '/no-organization', '/payment-success', ...POLICY_PAGE_PATH_LIST];

/** Mirrors JoinRequestGuard's BLOCKING set: an applicant in one of these states can't use
 *  the product yet, so there's no point walking them through device setup first — they'd
 *  land on /getting-started, finish it, and immediately hit the join-request wall anyway.
 *  Skipping FTUE here lets them see that wall (and its status/cooldown messaging)
 *  immediately instead. */
const JOIN_REQUEST_BLOCKING = new Set(['pending', 'locked_out', 'rejected']);

const FTUEGuard: React.FC<FTUEGuardProps> = ({ children }) => {
  const { t } = useTranslation();
  const { currentUser, token, loading, noOrganization } = useAuth();
  const location = useLocation();
  const { request: joinRequest, loading: joinRequestLoading } = useMyJoinRequest();
  const isBlockedByJoinRequest = !!joinRequest.status && JOIN_REQUEST_BLOCKING.has(joinRequest.status);
  const [ftueRequired, setFtueRequired] = useState<boolean | null>(null);
  const [checking, setChecking] = useState(true);
  const [checkError, setCheckError] = useState(false);
  // Message key, translated at render time.
  const [checkErrorKey, setCheckErrorKey] = useState('onboarding.unableToVerifySetup');

  // Only check once per user session (reset on user change)
  const ftueCheckedRef = useRef<string | null>(null);

  useEffect(() => {
    // Skip checks for public/skip pages
    if (SKIP_PATHS.includes(location.pathname) || location.pathname.startsWith('/auth/magic/')) {
      setFtueRequired(false);
      setChecking(false);
      setCheckError(false);
      return;
    }

    // Wait for auth to finish loading
    if (loading) return;

    // Users with no organization should hit OrganizationGuard, not FTUE.
    // Skip the FTUE check so we don't redirect to /getting-started before
    // the no-org wall can render.
    if (noOrganization) {
      setFtueRequired(false);
      setChecking(false);
      return;
    }

    // If no user after loading, they need to log in
    if (!currentUser || !token) {
      setChecking(false);
      return;
    }

    // Already checked for this user — skip
    if (ftueCheckedRef.current === currentUser.id) {
      setChecking(false);
      return;
    }

    const checkFTUE = async () => {
      try {
        setCheckError(false);
        const required = await checkFTUERequired(currentUser.id, token);
        setFtueRequired(required);
        ftueCheckedRef.current = currentUser.id;
      } catch (error) {
        const status = (error as { response?: { status?: number }; status?: number })?.response?.status
          ?? (error as { status?: number })?.status;
        console.error('[FTUEGuard] Failed to check FTUE status:', error);
        setCheckErrorKey(
          status === 401 || status === 403
            ? 'onboarding.sessionExpired'
            : 'onboarding.unableToVerifySetup'
        );
        setCheckError(true);
      } finally {
        setChecking(false);
      }
    };

    checkFTUE();
  }, [currentUser, token, loading, noOrganization, location.pathname]);

  // Reset check ref when user changes (logout/login)
  useEffect(() => {
    if (!currentUser) {
      ftueCheckedRef.current = null;
      setFtueRequired(null);
    }
  }, [currentUser]);

  // Don't block public/skip paths while auth loads — they must render immediately.
  // (e.g. /marketplace-signup must show the choice screen before the JWT expires)
  const isSkipPath = SKIP_PATHS.includes(location.pathname) || location.pathname.startsWith('/auth/magic/');
  if (!isSkipPath && (checking || loading || joinRequestLoading)) {
    return <LoadingIndicator message="" />;
  }

  if (checkError) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: '16px' }}>
        <p>{t(checkErrorKey)}</p>
        <button onClick={() => { setChecking(true); setCheckError(false); ftueCheckedRef.current = null; }}>
          {t('common.retry')}
        </button>
      </div>
    );
  }

  // Public pages — allow without auth
  const publicPaths = ['/login', '/signup', '/pricing', '/forgot-password', '/reset-password', '/sso', '/session-expired', '/marketplace-signup', ...POLICY_PAGE_PATH_LIST];
  const isPublicPage = publicPaths.includes(location.pathname) || location.pathname.startsWith('/auth/magic/');

  if (!isPublicPage && !currentUser) {
    try {
      sessionStorage.setItem('postLoginRedirect', location.pathname + location.search);
    } catch { /* storage blocked — route state is the fallback */ }
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (ftueRequired && !isBlockedByJoinRequest && location.pathname !== '/getting-started') {
    return <Navigate to="/getting-started" replace />;
  }

  return <>{children}</>;
};

export default FTUEGuard;
